import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import '../ExternalUsers/CreateExternalModal.css';

const SetPatientLimitModal = ({ isOpen, onClose, patientLimits, setPatientLimits }) => {
    const [date, setDate] = useState('');
    const [limit, setLimit] = useState('');
    const [error, setError] = useState(null);
    const [dentist, setDentist] = useState(''); // State for selected dentist
    const [dentistList, setDentistList] = useState([]); // State for list of dentists

    // Fetch dentists from the API
    useEffect(() => {
        const fetchDentists = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/dentists');
                if (!response.ok) {
                    throw new Error('Failed to fetch dentists');
                }
                const dentists = await response.json();
                setDentistList(dentists); // Store the fetched dentists
            } catch (error) {
                console.error('Error fetching dentists:', error);
            }
        };

        fetchDentists();
    }, []);

    const handleSetPatientLimit = async () => {
        if (!date || !limit) {
            setError('Please select a date and enter a patient limit.');
            return;
        }

        // Check if the selected date is before today
        const selectedDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison

        if (selectedDate < today) {
            setError('You cannot select a date before today.');
            return;
        }

        // Check if the date already has a patient limit
        const existingLimit = patientLimits.find((limitEvent) => {
            if (!limitEvent || !limitEvent.start) return false;
            return new Date(limitEvent.start).toISOString().split('T')[0] === date;
        });
        
        if (existingLimit) {
            setError('A patient limit is already set for this date.');
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/patientLimits', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ date, limit, dentist }),
            });

            if (response.ok) {
                // Alert with the patient limit and date
                window.alert(`Patient limit of ${limit} on ${date} set successfully!`);
            } else {
                throw new Error('Failed to save patient limit');
            }

            const newPatientLimit = await response.json();
            const updatedPatientLimits = [...patientLimits, newPatientLimit];
            setPatientLimits(updatedPatientLimits);
            setDate('');
            setLimit('');
            setError(null);
            onClose(); // Close the modal after setting the limit
        } catch (error) {
            console.error('Error saving patient limit:', error);
            setError('Failed to save patient limit. Please try again.');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="modal"
            overlayClassName="overlay"
        >
            <div className="modal-flex-div">
                <button className="close-icon" onClick={onClose}>&times;</button>
                <h2 className="h2-modal">Set Patient Limit</h2>
            </div>
            <div className="modal-divider" />

            {error && <div className="error-message">{error}</div>}

            {/* Dentist Field */}
            <div>
                <label>Dentist*</label>
                <select
                    value={dentist}
                    onChange={(e) => setDentist(e.target.value)}
                    className="modal-input"
                    required
                >
                    <option value="">Select Dentist</option>
                    {dentistList.map((dentist) => (
                        <option key={dentist._id} value={dentist.lastName}>
                            {dentist.firstName} {dentist.lastName}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label>Date*</label>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="modal-input"
                    min={new Date().toISOString().split('T')[0]} // Prevent selecting dates before today
                    required
                />
            </div>

            <div>
                <label>Patient Limit*</label>
                <input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="modal-input"
                    placeholder="Enter patient limit"
                    required
                />
            </div>

            <div className="padding-top" />
            <div className="modal-divider-bottom" />
            <div className="padding-top" />

            <button
                type="submit"
                onClick={handleSetPatientLimit}
            >
                Set Patient Limit
            </button>
        </Modal>
    );
};

export default SetPatientLimitModal;